/**
 * MaintenancePage Component
 * Elegant maintenance/error page shown when the backend is down
 * or when there's a critical application error
 */

import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  RefreshCw,
  Home,
  Mail,
  Settings,
  Coffee,
  Heart
} from 'lucide-react';

interface MaintenancePageProps {
  type?: 'maintenance' | 'error' | 'offline';
  error?: Error | null;
  onRetry?: () => void;
}

export function MaintenancePage({
  type = 'maintenance',
  error,
  onRetry
}: MaintenancePageProps) {
  const { t } = useTranslation();

  const handleRefresh = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const content = {
    maintenance: {
      icon: <Settings className="w-16 h-16 text-[#FBBF24]" />,
      title: t('maintenance.title', 'Maintenance in Progress'),
      subtitle: t('maintenance.subtitle', 'We\'re improving your experience'),
      description: t(
        'maintenance.description',
        'Our team is currently working to improve the site. We\'ll be back very soon!'
      ),
    },
    error: {
      icon: <Coffee className="w-16 h-16 text-[#FBBF24]" />,
      title: t('maintenance.errorTitle', 'Oops, technical hiccup'),
      subtitle: t('maintenance.errorSubtitle', 'It\'s not you, it\'s us'),
      description: t(
        'maintenance.errorDescription',
        'An unexpected error occurred. Our team is on it!'
      ),
    },
    offline: {
      icon: <Heart className="w-16 h-16 text-[#FBBF24]" />,
      title: t('maintenance.offlineTitle', 'Offline Mode'),
      subtitle: t('maintenance.offlineSubtitle', 'You\'re disconnected'),
      description: t(
        'maintenance.offlineDescription',
        'Check your Internet connection and try again.'
      ),
    },
  };

  const currentContent = content[type];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#0a1628] to-slate-900 flex items-center justify-center p-4 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating circles */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#1E40AF]/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#FBBF24]/10 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative max-w-lg w-full text-center"
      >
        {/* Logo */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', damping: 12 }}
          className="mb-8"
        >
          <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 shadow-2xl">
            <motion.div
              animate={{
                rotate: type === 'maintenance' ? [0, 360] : 0,
              }}
              transition={{
                duration: 20,
                repeat: type === 'maintenance' ? Infinity : 0,
                ease: 'linear',
              }}
            >
              {currentContent.icon}
            </motion.div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-3xl sm:text-4xl font-bold text-white mb-4"
        >
          {currentContent.title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-lg text-[#FBBF24] font-medium mb-4"
        >
          {currentContent.subtitle}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-slate-600 mb-8 leading-relaxed"
        >
          {currentContent.description}
        </motion.p>

        {/* Error details (dev only) */}
        {error && import.meta.env.DEV && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mb-8 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-left"
          >
            <p className="text-xs font-mono text-red-400 break-all">
              {error.name}: {error.message}
            </p>
          </motion.div>
        )}

        {/* Action buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleRefresh}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-slate-900 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            {t('common.tryAgain', 'Try again')}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleGoHome}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full font-semibold border border-white/20 transition-all"
          >
            <Home className="w-4 h-4" />
            {t('errors.boundary.goHome', 'Home')}
          </motion.button>
        </motion.div>

        {/* Contact info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-12 pt-8 border-t border-white/10"
        >
          <p className="text-slate-600 text-sm mb-3">
            {t('maintenance.contactUs', 'Need help? Contact us')}
          </p>
          <a
            href="mailto:contact@yimburundi.com"
            className="inline-flex items-center gap-2 text-[#FBBF24] hover:text-[#FCD34D] transition-colors"
          >
            <Mail className="w-4 h-4" />
            contact@yimburundi.com
          </a>
        </motion.div>

        {/* YIM Logo/Branding */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8"
        >
          <p className="text-slate-600 text-xs font-medium">
            <span className="text-[#FBBF24]">YIM</span> Burundi
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default MaintenancePage;

